// Pricing v2 — informational pricing page. No payment / checkout integration.
// All CTAs route to the existing trial signup handoff (my.tabletalk.app) or to
// the contact page. Pricing data lives in `src/config/pricingV2.js`.
//
// Layout: Hero → Proof band → Three package cards → Everything-included
// features → Chain volume discounts → Add-ons → NFC cards → Fairness statement
// → FAQ → Final CTA. Locale-driven via the URL — RON for /ro/, EUR everywhere
// else; USD slots in by adding a config entry only.

import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Icon from "../components/icons.jsx";
import { Eyebrow, FaqItem } from "../components/ui.jsx";
import { useLocaleNav, useCurrentLocale } from "../lib/useLocaleNav.js";
import { goToSignup } from "../lib/handoff.js";
import { getCountry } from "../lib/geo.js";
import { pricingV2ForLocale, formatV2Price } from "../config/pricingV2.js";
import { track } from "../lib/analytics.js";

// Inline document-meta hook — we don't have react-helmet here. Sets title and
// the description meta on mount, restores on unmount so the homepage default
// returns when the user navigates away.
function useDocumentMeta({ title, description }) {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = title;
    let descTag = document.querySelector('meta[name="description"]');
    let createdDesc = false;
    if (!descTag) {
      descTag = document.createElement("meta");
      descTag.setAttribute("name", "description");
      document.head.appendChild(descTag);
      createdDesc = true;
    }
    const prevDesc = descTag.getAttribute("content");
    descTag.setAttribute("content", description);
    return () => {
      document.title = prevTitle;
      if (createdDesc) descTag.remove();
      else if (prevDesc != null) descTag.setAttribute("content", prevDesc);
    };
  }, [title, description]);
}

const META_BY_LOCALE = {
  en: {
    title: "Pricing — TableTalk. Fair hospitality, fairly priced.",
    description:
      "Same platform for every restaurant. Priced by the size of your operation. Three packages — Local, Standard, High-Volume — plus volume discounts for chains.",
  },
  ro: {
    title: "Prețuri — TableTalk. Ospitalitate corectă, preț corect.",
    description:
      "Aceeași platformă pentru fiecare restaurant. Preț în funcție de dimensiunea operațiunii tale. Trei pachete și discounturi pentru lanțuri.",
  },
  es: {
    title: "Precios — TableTalk. Hospitalidad justa, precio justo.",
    description:
      "La misma plataforma para cada restaurante. Precio según el tamaño de tu operación. Tres paquetes y descuentos por volumen.",
  },
  de: {
    title: "Preise — TableTalk. Faire Gastfreundschaft, fair bepreist.",
    description:
      "Die gleiche Plattform für jedes Restaurant. Preis nach Größe deines Betriebs. Drei Pakete und Mengenrabatte für Ketten.",
  },
  fr: {
    title: "Tarifs — TableTalk. Une hospitalité juste, à prix juste.",
    description:
      "La même plateforme pour chaque restaurant. Prix selon la taille de votre exploitation. Trois packages et remises sur volume.",
  },
  it: {
    title: "Prezzi — TableTalk. Ospitalità giusta, prezzo giusto.",
    description:
      "La stessa piattaforma per ogni ristorante. Prezzo in base alla dimensione della tua attività. Tre pacchetti e sconti volume.",
  },
};

export default function PricingV2() {
  const locale = useCurrentLocale();
  const meta = META_BY_LOCALE[locale] || META_BY_LOCALE.en;
  useDocumentMeta(meta);

  const cfg = pricingV2ForLocale(locale);
  const go = useLocaleNav();
  const [billing, setBilling] = useState("yearly");
  const [country, setCountry] = useState(null);

  useEffect(() => {
    let mounted = true;
    getCountry().then(c => { if (mounted) setCountry(c); }).catch(() => {});
    return () => { mounted = false; };
  }, []);

  const handleToggleBilling = (next) => {
    if (next === billing) return;
    track("pricing_v2_billing_changed", { billing: next });
    setBilling(next);
  };

  const handleStartTrial = (packageId) => () => {
    track("pricing_v2_cta_clicked", { package: packageId, billing });
    goToSignup({ locale, planId: packageId, country });
  };

  const handleTalkToSales = () => {
    track("pricing_v2_talk_to_sales_clicked");
    go("contact");
  };

  const handleBuyNfc = (packId) => () => {
    track("pricing_v2_nfc_buy_clicked", { pack: packId });
    go("contact");
  };

  const handleClaimFreeWeek = () => {
    track("pricing_v2_claim_free_week_clicked");
    goToSignup({ locale, country });
  };

  // VAT disclosure ("+ VAT") is only shown for the Romanian locale. Every other
  // locale on this page hides VAT lines across plan cards, add-ons, and NFC
  // packs.
  const showVat = locale === "ro";

  return (
    <main>
      <Hero />
      <PackageCards cfg={cfg} billing={billing} showVat={showVat} onToggleBilling={handleToggleBilling} onChoose={handleStartTrial} />
      <EverythingIncluded />
      <ChainPricing onTalkToSales={handleTalkToSales} />
      <Addons cfg={cfg} showVat={showVat} />
      <NfcCardsSection cfg={cfg} showVat={showVat} onBuy={handleBuyNfc} />
      <FairnessStatement />
      <FAQ />
      <FinalCTA onClaim={handleClaimFreeWeek} />
    </main>
  );
}

/* ------------------------------------------------------------------ HERO -- */

function Hero() {
  const { t } = useTranslation();
  return (
    <section style={{ background: "var(--bg)" }}>
      <div className="container" style={{ padding: "56px 24px 96px" }}>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 12, fontFamily: "Inter, sans-serif", fontSize: 12, fontWeight: 500, letterSpacing: ".22em", textTransform: "uppercase", color: "var(--persimmon)" }}>
          <span style={{ width: 22, height: 1, background: "var(--persimmon)" }} />
          {t("pricingV2.hero.eyebrow")}
        </div>
        <h1 className="tt-h tt-h-xl" style={{ marginTop: 24 }}>
          {t("pricingV2.hero.headline")}<br />
          <span style={{ fontFamily: "Fraunces, serif", fontStyle: "italic", fontWeight: 400 }}>{t("pricingV2.hero.headlineItalic")}</span>
        </h1>
        <p style={{ marginTop: 32, maxWidth: 720, fontSize: "clamp(18px, 1.4vw, 21px)", lineHeight: 1.6, color: "var(--ink-2)" }}>
          {t("pricingV2.hero.intro")}
        </p>
      </div>
    </section>
  );
}

function BillingToggle({ billing, onToggle }) {
  const { t } = useTranslation();
  const pill = (key, label, sub) => {
    const active = billing === key;
    return (
      <button
        key={key}
        type="button"
        onClick={() => onToggle(key)}
        aria-pressed={active}
        style={{
          all: "unset",
          cursor: "pointer",
          padding: "12px 22px",
          borderRadius: 999,
          background: active ? "var(--ink)" : "transparent",
          color: active ? "var(--bg)" : "var(--ink)",
          fontFamily: "Inter, sans-serif",
          fontWeight: 600,
          fontSize: 14.5,
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          transition: "background .2s ease, color .2s ease",
        }}
      >
        {label}
        {sub && (
          <span style={{
            fontWeight: 500,
            fontSize: 12,
            letterSpacing: ".08em",
            textTransform: "uppercase",
            color: active ? "var(--marigold)" : "var(--persimmon)",
          }}>
            {sub}
          </span>
        )}
      </button>
    );
  };
  return (
    <div role="group" aria-label={t("pricingV2.billingAria")}
      style={{ display: "inline-flex", padding: 4, background: "var(--bg)", border: "1px solid var(--line)", borderRadius: 999 }}>
      {pill("monthly", t("pricingV2.billingMonthly"))}
      {pill("yearly", t("pricingV2.billingYearly"), t("pricingV2.billingSaveBadge"))}
    </div>
  );
}

/* ----------------------------------------------------- THREE PACKAGE CARDS */

const PACKAGE_IDS = ["local", "standard", "high_volume"];
const FEATURED_INDEX = 1; // Standard

function PackageCards({ cfg, billing, showVat, onToggleBilling, onChoose }) {
  const { t } = useTranslation();
  const items = t("pricingV2.packages", { returnObjects: true });
  const packages = items.map((p, i) => ({
    id: PACKAGE_IDS[i],
    name: p.name,
    tagline: p.tagline,
    volume: p.volume,
    cards: p.cards,
    featured: i === FEATURED_INDEX
  }));
  return (
    <section style={{ padding: "80px 0 120px" }}>
      <div className="container">
        <div style={{ display: "flex", justifyContent: "center", marginBottom: 48 }}>
          <BillingToggle billing={billing} onToggle={onToggleBilling} />
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 24, alignItems: "stretch" }} className="pkg-grid">
          {packages.map(p => (
            <PackageCard key={p.id} pkg={p} cfg={cfg} billing={billing} showVat={showVat} onChoose={onChoose(p.id)} />
          ))}
        </div>
      </div>
      <style>{`@media (max-width: 980px) { .pkg-grid { grid-template-columns: 1fr !important; } }`}</style>
    </section>
  );
}

function PackageCard({ pkg, cfg, billing, showVat, onChoose }) {
  const { t } = useTranslation();
  const featured = !!pkg.featured;
  const monthly = cfg.packages[pkg.id].monthly;
  const yearly = cfg.packages[pkg.id].yearly;
  const amount = billing === "yearly" ? yearly : monthly;
  const priceLabel = formatV2Price(amount, cfg);

  return (
    <div style={{
      position: "relative",
      background: featured ? "var(--ink)" : "var(--bg)",
      color: featured ? "var(--bg)" : "var(--ink)",
      border: featured ? "1px solid var(--ink)" : "1px solid var(--line)",
      borderRadius: 6,
      padding: "44px 32px 36px",
      display: "flex",
      flexDirection: "column",
      boxShadow: featured ? "0 24px 60px -32px rgba(16,14,12,0.35)" : "none",
    }}>
      {featured && (
        <div style={{
          position: "absolute",
          top: -14,
          left: "50%",
          transform: "translateX(-50%)",
          background: "var(--persimmon)",
          color: "var(--bg)",
          fontFamily: "Inter, sans-serif",
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: ".16em",
          textTransform: "uppercase",
          padding: "8px 16px",
          borderRadius: 999,
          whiteSpace: "nowrap",
        }}>
          {t("pricingV2.mostPopular")}
        </div>
      )}

      <div style={{
        fontFamily: "Inter, sans-serif", fontSize: 11.5, fontWeight: 600,
        letterSpacing: ".22em", textTransform: "uppercase",
        color: featured ? "var(--marigold)" : "var(--persimmon)",
      }}>
        {pkg.name}
      </div>

      <p style={{
        margin: "14px 0 0",
        fontFamily: "Inter, sans-serif", fontSize: 14.5, lineHeight: 1.55,
        color: featured ? "rgba(255,255,255,.78)" : "var(--ink-2)",
        minHeight: 44,
      }}>
        {pkg.tagline}
      </p>

      <div style={{ marginTop: 28, display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
        <span className="tt-big" style={{ fontSize: "clamp(48px, 6vw, 72px)", color: featured ? "var(--marigold)" : "var(--persimmon)", lineHeight: 0.9 }}>
          {priceLabel}
        </span>
        <span style={{ fontSize: 14, color: featured ? "rgba(255,255,255,.65)" : "var(--muted)" }}>
          {t("pricingV2.perMonthPerLocation")}
        </span>
      </div>
      {showVat && (
        <div style={{ marginTop: 6, fontSize: 12, color: featured ? "rgba(255,255,255,.55)" : "var(--muted)", letterSpacing: ".04em" }}>
          {t("pricingV2.plusVat")}
        </div>
      )}

      <div style={{
        marginTop: 24,
        display: "inline-flex", alignSelf: "flex-start",
        alignItems: "center", gap: 8,
        padding: "6px 12px",
        borderRadius: 999,
        background: featured ? "rgba(217,162,59,.18)" : "rgba(255,105,45,.10)",
        color: featured ? "var(--marigold)" : "var(--persimmon)",
        fontFamily: "Inter, sans-serif", fontSize: 12, fontWeight: 600, letterSpacing: ".06em",
      }}>
        <span aria-hidden="true">★</span> {t("pricingV2.freeWeekBadge")}
      </div>

      <ul style={{ listStyle: "none", padding: "28px 0 0", margin: 0, display: "flex", flexDirection: "column", gap: 14, flex: 1 }}>
        {[pkg.volume, pkg.cards].map(line => (
          <li key={line} style={{ display: "flex", gap: 12, alignItems: "flex-start", fontSize: 15, lineHeight: 1.5 }}>
            <span aria-hidden="true" style={{
              width: 6, height: 6, borderRadius: 999, marginTop: 9,
              background: featured ? "var(--marigold)" : "var(--persimmon)",
              flexShrink: 0,
            }} />
            <span>{line}</span>
          </li>
        ))}
      </ul>

      <button
        type="button"
        onClick={onChoose}
        className="btn"
        style={{
          marginTop: 32,
          width: "100%",
          background: featured ? "var(--persimmon)" : "var(--ink)",
          color: "var(--bg)",
        }}
      >
        {t("pricingV2.startTrial")} <Icon name="arrow-right" size={16} />
      </button>
    </div>
  );
}

/* --------------------------------------------------- EVERYTHING INCLUDED -- */

function EverythingIncluded() {
  const { t } = useTranslation();
  const col1 = t("pricingV2.included.col1", { returnObjects: true });
  const col2 = t("pricingV2.included.col2", { returnObjects: true });
  return (
    <section style={{ background: "var(--bg-2)", padding: "120px 0", borderTop: "1px solid var(--line)" }}>
      <div className="container">
        <Eyebrow>{t("pricingV2.included.eyebrow")}</Eyebrow>
        <h2 className="tt-h tt-h-lg" style={{ marginTop: 20, maxWidth: 900 }}>
          {t("pricingV2.included.headline")}{" "}
          <span style={{ color: "var(--persimmon)" }}>{t("pricingV2.included.headlineSpan")}</span>
        </h2>
        <div style={{ marginTop: 56, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 48 }} className="feat-grid">
          <FeatureColumn items={col1} />
          <FeatureColumn items={col2} />
        </div>
      </div>
      <style>{`@media (max-width: 760px) { .feat-grid { grid-template-columns: 1fr !important; gap: 16px !important; } }`}</style>
    </section>
  );
}

function FeatureColumn({ items }) {
  return (
    <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 18 }}>
      {items.map(x => (
        <li key={x} style={{ display: "flex", gap: 14, alignItems: "flex-start", fontSize: 16, lineHeight: 1.55, color: "var(--ink)" }}>
          <span aria-hidden="true" style={{ width: 7, height: 7, borderRadius: 999, marginTop: 10, background: "var(--persimmon)", flexShrink: 0 }} />
          <span>{x}</span>
        </li>
      ))}
    </ul>
  );
}

/* ----------------------------------------------------------- CHAIN PRICING */

function ChainPricing({ onTalkToSales }) {
  const { t } = useTranslation();
  const rows = t("pricingV2.chain.rows", { returnObjects: true });
  return (
    <section style={{ padding: "120px 0", borderBottom: "1px solid var(--line)" }}>
      <div className="container">
        <Eyebrow>{t("pricingV2.chain.eyebrow")}</Eyebrow>
        <h2 className="tt-h tt-h-lg" style={{ marginTop: 20, maxWidth: 1000 }}>
          {t("pricingV2.chain.headline")}{" "}
          <span style={{ color: "var(--persimmon)" }}>{t("pricingV2.chain.headlineSpan")}</span>
        </h2>
        <p style={{ marginTop: 20, maxWidth: 720, fontSize: "clamp(17px, 1.3vw, 20px)", color: "var(--ink-2)", lineHeight: 1.6 }}>
          {t("pricingV2.chain.intro")}
        </p>

        <div style={{ marginTop: 48, maxWidth: 760, border: "1px solid var(--line)", borderRadius: 6, overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: "Inter, sans-serif" }}>
            <thead>
              <tr style={{ background: "var(--bg-2)" }}>
                <th scope="col" style={cellHead}>{t("pricingV2.chain.colLocations")}</th>
                <th scope="col" style={cellHead}>{t("pricingV2.chain.colDiscount")}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(([locs, disc], i) => (
                <tr key={locs} style={{ borderTop: "1px solid var(--line)" }}>
                  <td style={cellBody}>{locs}</td>
                  <td style={{ ...cellBody, fontWeight: disc.startsWith("–") ? 700 : 500, color: disc.startsWith("–") ? "var(--persimmon)" : "var(--ink)" }}>
                    {disc}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div style={{ marginTop: 36 }}>
          <button type="button" onClick={onTalkToSales} className="btn btn-lg" style={{ background: "var(--ink)", color: "var(--bg)" }}>
            {t("pricingV2.chain.cta")} <Icon name="arrow-right" size={16} />
          </button>
        </div>
      </div>
    </section>
  );
}

const cellHead = {
  textAlign: "left",
  padding: "16px 20px",
  fontSize: 12,
  fontWeight: 600,
  letterSpacing: ".16em",
  textTransform: "uppercase",
  color: "var(--ink-2)",
};
const cellBody = {
  padding: "18px 20px",
  fontSize: 15.5,
  color: "var(--ink)",
};

/* ----------------------------------------------------------------- ADDONS */

function Addons({ cfg, showVat }) {
  const { t } = useTranslation();
  // Mounted-once tracking — gives a baseline impression metric for the section.
  useEffect(() => { track("pricing_v2_addon_viewed"); }, []);

  const items = t("pricingV2.addons.items", { returnObjects: true });
  const cfgKeys = ["review_reply", "live_chat"];
  const addons = items.map((item, i) => ({
    id: cfgKeys[i],
    name: item.name,
    desc: item.desc,
    amount: cfg.addons[cfgKeys[i]]
  }));
  return (
    <section style={{ background: "var(--bg-2)", padding: "120px 0", borderTop: "1px solid var(--line)" }}>
      <div className="container">
        <Eyebrow>{t("pricingV2.addons.eyebrow")}</Eyebrow>
        <h2 className="tt-h tt-h-lg" style={{ marginTop: 20, maxWidth: 900 }}>
          {t("pricingV2.addons.headline")}{" "}
          <span style={{ color: "var(--persimmon)" }}>{t("pricingV2.addons.headlineSpan")}</span>
        </h2>
        <div style={{ marginTop: 56, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }} className="addon-grid">
          {addons.map(a => (
            <div key={a.id} style={{
              background: "var(--bg)",
              border: "1px solid var(--line)",
              borderRadius: 6,
              padding: "32px",
              display: "flex",
              flexDirection: "column",
              gap: 16,
            }}>
              <div style={{
                alignSelf: "flex-start",
                fontFamily: "Inter, sans-serif",
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: ".16em",
                textTransform: "uppercase",
                color: "var(--marigold)",
                background: "rgba(217,162,59,.12)",
                padding: "6px 10px",
                borderRadius: 999,
              }}>
                {t("pricingV2.addons.comingSoon")}
              </div>
              <div className="tt-h tt-h-md" style={{ fontSize: 26 }}>{a.name}</div>
              <p style={{ margin: 0, fontSize: 15.5, color: "var(--ink-2)", lineHeight: 1.6 }}>{a.desc}</p>
              <div style={{ marginTop: 8, display: "flex", alignItems: "baseline", gap: 8 }}>
                <span className="tt-big" style={{ fontSize: 40, color: "var(--persimmon)", lineHeight: 0.9 }}>
                  +{formatV2Price(a.amount, cfg)}
                </span>
                <span style={{ fontSize: 13, color: "var(--muted)" }}>{t("pricingV2.addons.perLocationMonth")}</span>
              </div>
              {showVat && <div style={{ fontSize: 12, color: "var(--muted)" }}>{cfg.vatLabel}</div>}
            </div>
          ))}
        </div>
      </div>
      <style>{`@media (max-width: 760px) { .addon-grid { grid-template-columns: 1fr !important; } }`}</style>
    </section>
  );
}

/* -------------------------------------------------------------- NFC CARDS */

const NFC_STD_IDS = ["essential", "standard", "pro"];
const NFC_STD_CFG_KEYS = ["essential", "standard", "pro"];
const NFC_CUSTOM_IDS = ["essential_custom", "standard_custom", "pro_custom"];
const NFC_CUSTOM_CFG_KEYS = ["essential_custom", "standard_custom", "pro_custom"];

function NfcCardsSection({ cfg, showVat, onBuy }) {
  const { t } = useTranslation();
  const stdItems = t("pricingV2.nfc.standard", { returnObjects: true });
  const customItems = t("pricingV2.nfc.custom", { returnObjects: true });
  const standardPacks = stdItems.map((it, i) => ({ id: NFC_STD_IDS[i], name: it.name, desc: it.desc, cfgKey: NFC_STD_CFG_KEYS[i] }));
  const customPacks = customItems.map((it, i) => ({ id: NFC_CUSTOM_IDS[i], name: it.name, desc: it.desc, cfgKey: NFC_CUSTOM_CFG_KEYS[i], custom: true }));
  return (
    <section style={{ padding: "120px 0", borderBottom: "1px solid var(--line)" }}>
      <div className="container">
        <Eyebrow>{t("pricingV2.nfc.eyebrow")}</Eyebrow>
        <h2 className="tt-h tt-h-lg" style={{ marginTop: 20, maxWidth: 1000 }}>
          {t("pricingV2.nfc.headline")}{" "}
          <span style={{ color: "var(--persimmon)" }}>{t("pricingV2.nfc.headlineSpan")}</span>
        </h2>

        <div style={{ marginTop: 56, display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 24 }} className="nfc-grid">
          {standardPacks.map(p => (
            <NfcPackCard key={p.id} pack={p} amount={cfg.nfc_cards[p.cfgKey]} cfg={cfg} showVat={showVat} onBuy={onBuy(p.id)} />
          ))}
        </div>

        <div style={{ marginTop: 64, fontFamily: "Inter, sans-serif", fontSize: 12, fontWeight: 600, letterSpacing: ".22em", textTransform: "uppercase", color: "var(--ink-2)" }}>
          {t("pricingV2.nfc.customHeader")}
        </div>
        <div style={{ marginTop: 24, display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 24 }} className="nfc-grid">
          {customPacks.map(p => (
            <NfcPackCard key={p.id} pack={p} amount={cfg.nfc_cards[p.cfgKey]} cfg={cfg} showVat={showVat} onBuy={onBuy(p.id)} />
          ))}
        </div>
      </div>
      <style>{`
        @media (max-width: 960px) { .nfc-grid { grid-template-columns: 1fr 1fr !important; } }
        @media (max-width: 620px) { .nfc-grid { grid-template-columns: 1fr !important; } }
      `}</style>
    </section>
  );
}

function NfcPackCard({ pack, amount, cfg, showVat, onBuy }) {
  const { t } = useTranslation();
  return (
    <div style={{
      border: "1px solid var(--line)",
      borderRadius: 6,
      overflow: "hidden",
      background: "var(--bg)",
      display: "flex",
      flexDirection: "column",
    }}>
      <NfcCardVisual custom={!!pack.custom} />
      <div style={{ padding: "28px 28px 32px", display: "flex", flexDirection: "column", gap: 12 }}>
        <div style={{
          fontFamily: "Satoshi, Inter, sans-serif", fontWeight: 700,
          fontSize: 22, letterSpacing: "-0.02em",
          color: "var(--persimmon)",
        }}>
          {pack.name}
        </div>
        <div style={{ fontSize: 14.5, color: "var(--ink-2)" }}>{pack.desc}</div>
        <div style={{ marginTop: 8, display: "flex", alignItems: "baseline", gap: 8 }}>
          <span className="tt-big" style={{ fontSize: 40, color: "var(--ink)", lineHeight: 0.9 }}>
            {formatV2Price(amount, cfg)}
          </span>
          <span style={{ fontSize: 13, color: "var(--muted)" }}>{showVat ? t("pricingV2.nfc.perPackWithVat", { vat: cfg.vatLabel }) : t("pricingV2.nfc.perPack")}</span>
        </div>
        <button type="button" onClick={onBuy} className="btn" style={{ marginTop: 12, alignSelf: "flex-start", background: "var(--ink)", color: "var(--bg)" }}>
          {t("common.buy")} <Icon name="arrow-right" size={14} />
        </button>
      </div>
    </div>
  );
}

// Product photo for an NFC pack. Two photos cover both rows:
//   standard_tabletalk_cards-768.webp — TableTalk-branded cards (top row)
//   custom_tabletalk_cards-768.webp   — generic "YOUR LOGO" cards (bottom row)
function NfcCardVisual({ custom = false }) {
  const { t } = useTranslation();
  const src = custom
    ? "/assets/custom_tabletalk_cards-768.webp"
    : "/assets/standard_tabletalk_cards-768.webp";
  const alt = custom
    ? t("pricingV2.nfc.altCustom")
    : t("pricingV2.nfc.altStandard");
  return (
    <div style={{
      aspectRatio: "16/10",
      background: "#3D3D3D",
      position: "relative",
      overflow: "hidden",
    }}>
      <img
        src={src}
        alt={alt}
        loading="lazy"
        decoding="async"
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
          display: "block",
        }}
      />
    </div>
  );
}

/* ---------------------------------------------------- FAIRNESS STATEMENT -- */

function FairnessStatement() {
  const { t } = useTranslation();
  return (
    <section style={{ background: "rgba(255,105,45,0.06)", padding: "120px 24px", borderTop: "1px solid var(--line)", borderBottom: "1px solid var(--line)" }}>
      <div style={{ maxWidth: 700, margin: "0 auto", textAlign: "center" }}>
        <Eyebrow>{t("pricingV2.fairness.eyebrow")}</Eyebrow>
        <p style={{ marginTop: 24, fontFamily: "Inter, sans-serif", fontSize: "clamp(17px, 1.3vw, 19px)", lineHeight: 1.7, color: "var(--ink)" }}>
          <strong style={{ fontWeight: 700 }}>{t("pricingV2.fairness.boldPrefix")}</strong>{" "}
          {t("pricingV2.fairness.body")}
        </p>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------- FAQ -- */

function FAQ() {
  const { t } = useTranslation();
  const faqs = t("pricingV2.faqs", { returnObjects: true });
  return (
    <section style={{ padding: "120px 0", borderBottom: "1px solid var(--line)" }}>
      <div className="container">
        <Eyebrow>{t("pricingV2.faqEyebrow")}</Eyebrow>
        <h2 className="tt-h tt-h-lg" style={{ marginTop: 20, marginBottom: 48 }}>
          {t("pricingV2.faqHeadline")}{" "}
          <span style={{ color: "var(--persimmon)" }}>{t("pricingV2.faqHeadlineSpan")}</span>
        </h2>
        {faqs.map((f, i) => (
          <TrackedFaqItem key={i} q={f.q} a={f.a} />
        ))}
      </div>
    </section>
  );
}

// Wraps FaqItem so the first open of each question fires an analytics event.
function TrackedFaqItem({ q, a }) {
  const [tracked, setTracked] = useState(false);
  const handleClick = () => {
    if (!tracked) {
      track("pricing_v2_faq_opened", { question: q });
      setTracked(true);
    }
  };
  return (
    <div onClick={handleClick} role="presentation">
      <FaqItem q={q} a={a} />
    </div>
  );
}

/* ------------------------------------------------------------- FINAL CTA -- */

function FinalCTA({ onClaim }) {
  const { t } = useTranslation();
  return (
    <section className="bg-bold" style={{ padding: "140px 0" }}>
      <div className="container">
        <h2 className="tt-h tt-h-xl" style={{ color: "var(--ink)", maxWidth: 1100 }}>
          {t("pricingV2.finalCta.headline")}
        </h2>
        <p style={{ marginTop: 20, fontSize: "clamp(18px, 1.4vw, 21px)", color: "var(--ink)", opacity: .8, maxWidth: 600 }}>
          {t("common.freeTrialNote")}
        </p>
        <div style={{ marginTop: 40 }}>
          <button type="button" onClick={onClaim} className="btn btn-lg" style={{ background: "var(--ink)", color: "var(--bg)" }}>
            {t("common.claimFreeWeek")} <Icon name="arrow-right" size={16} />
          </button>
        </div>
      </div>
    </section>
  );
}
